import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {LabelRow$key} from './__generated__/LabelRow.graphql'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ActionList, Link, Tooltip} from '@primer/react'
import {LabelToken} from '@github-ui/label-token'
import {GitPullRequestIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import styles from './RepositoryLabel.module.css'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'

type LabelRowProps = {
  label: LabelRow$key
}

export function LabelRow({label}: LabelRowProps) {
  const data = useFragment(
    graphql`
      fragment LabelRow on Label {
        id
        name
        nameHTML
        color
        description
        issues {
          totalCount
        }
        pullRequests {
          totalCount
        }
      }
    `,
    label,
  )

  const openIssues = data.issues?.totalCount || 0
  const openIssuesQuery = `issues?q=${encodeURIComponent(`is:open label:"${data.name}"`)}`
  const openPullRequests = data.pullRequests?.totalCount || 0
  const openPullRequestsQuery = `issues?q=${encodeURIComponent(`is:open is:pr label:"${data.name}"`)}`

  return (
    <ListItem
      key={data.id}
      title={<></>} // We need to solve this. title is important for announcing the list item but we don't have a title concept here.
      role="listitem"
      secondaryActions={
        <ListItemActionBar
          staticMenuActions={[
            {
              key: 'Edit',
              render: () => <ActionList.Item>Edit</ActionList.Item>,
            },
            {
              key: 'Delete',
              render: () => <ActionList.Item>Delete</ActionList.Item>,
            },
            {
              // Not all labels can be converted to discussions - find out how to check this
              key: 'convert-to-discussion',
              render: () => <ActionList.Item>Convert to discussion</ActionList.Item>,
            },
          ]}
        />
      }
    >
      <ListItemMainContent>
        <ListItemDescription className={styles.labelRowDescription}>
          <ListItemDescriptionItem>
            <Link href={`labels/${encodeURIComponent(data.name)}`} muted>
              <LabelToken
                text={<SafeHTMLText html={data.nameHTML as SafeHTMLString} />}
                key={0}
                fillColor={`#${data.color}`}
                // LabelToken does not support className yet.
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  maxWidth: '100%',
                }}
              />
            </Link>{' '}
          </ListItemDescriptionItem>
          <ListItemDescriptionItem>{data.description}</ListItemDescriptionItem>
          <ListItemDescriptionItem className={styles.labelRowIssuesAndPrsCount}>
            {openIssues > 0 ? (
              <Tooltip text={`${openIssues} open issues`} direction="n">
                <Link href={openIssuesQuery} muted>
                  <IssueOpenedIcon size={16} /> {openIssues}
                </Link>
              </Tooltip>
            ) : null}
            {openPullRequests > 0 ? (
              <Tooltip text={`${openPullRequests} open pull requests`} direction="n">
                <Link href={openPullRequestsQuery} muted>
                  <GitPullRequestIcon size={16} /> {openPullRequests}
                </Link>
              </Tooltip>
            ) : null}
          </ListItemDescriptionItem>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

try{ LabelRow.displayName ||= 'LabelRow' } catch {}